import React, { useState } from "react";
import { Field, getFormValues, reduxForm } from "redux-form";
import { connect } from "react-redux";
import {
  processFormValuesPatch,
  processFormValuesPost,
  renderFormInput,
} from "../../modules/formTools";

import SmartSelect from "../Form/SmartSelect";
import { ButtonPrimary } from "../Button";
import Loading from "../Loading";
import moment from "moment";
import { Toggle } from "../../ui";
import { Checkbox, Input } from "../Form";
import { email, optional, required } from "../../modules/validation";
import DropzoneArea from "../Form/File/DropzoneArea";
import _, { isArray } from "lodash";
import { uploadCorporateAccountLogoImage } from "../../redux/actions";
import { getChangedValues } from "../../utils";

const v = {
  required: required(),
  email: [optional(email)],
};

const colourSchemeOptions = [
  {
    label: "Purple",
    value: "corporate-admin--purple",
  },
  {
    label: "Red",
    value: "corporate-admin--red",
  },
  {
    label: "Green",
    value: "corporate-admin--green",
  },
];

const getInitialValues = corporateAccount => {
  if (!corporateAccount) return null;
  let reviewItems = [];
  let placeHolderDate = null;
  let bookingDeskIvOptions = [];
  let optionalPermissionsIvOptions = [];
  if (typeof corporateAccount !== "undefined") {
    if (corporateAccount.financial_year_start_month) {
      placeHolderDate = moment()
        .startOf("year")
        .set("month", corporateAccount.financial_year_start_month - 1);
    } else {
      placeHolderDate = moment().startOf("year");
    }
    if (corporateAccount.requires_abpi) {
      reviewItems.push("requires_abpi");
    }
    if (corporateAccount.internal_external_split) {
      reviewItems.push("internal_external_split");
    }
    if (typeof corporateAccount.booking_desks !== "undefined") {
      corporateAccount.booking_desks.map(desk => {
        bookingDeskIvOptions.push({
          label: desk.name,
          value: desk.booking_desk_id,
        });
      });
    }
    if (typeof corporateAccount.optional_permissions !== "undefined") {
      corporateAccount.optional_permissions.map(
        ({ display_name, permission }) => {
          optionalPermissionsIvOptions.push({
            label: display_name,
            value: permission,
          });
        }
      );
    }
  }

  return {
    company:
      corporateAccount && corporateAccount.company
        ? {
            label: corporateAccount.company.name,
            value: corporateAccount.company.id,
          }
        : null,
    account_name: corporateAccount.account_name,
    authorising_email: corporateAccount.authorising_email,
    booking_desks: bookingDeskIvOptions,
    corporate_account_permissions: optionalPermissionsIvOptions,
    max_delegates_triggers_authorisation_workflow:
      corporateAccount.max_delegates_triggers_authorisation_workflow,
    show_internal_external_on_brief:
      corporateAccount.show_internal_external_on_brief,
    is_pharmaceutical: corporateAccount.is_pharmaceutical,
    export_net: corporateAccount.export_net,
    email_notification_override: corporateAccount.email_notification_override,
    client_facing_email_notification_override:
      corporateAccount.client_facing_email_notification_override,
    venue_facing_email_notification_override:
      corporateAccount.venue_facing_email_notification_override,
    review_items: reviewItems,
    financial_year_start_month: placeHolderDate
      ? {
          label: placeHolderDate.format("MMMM"),
          value: placeHolderDate.format("M"),
        }
      : null,
    colour_scheme_classname: corporateAccount?.colour_scheme_classname
      ? colourSchemeOptions.filter(
          opt => opt.value === corporateAccount.colour_scheme_classname
        )
      : colourSchemeOptions.filter(opt => opt.value === ""),
  };
};

const mapStateToProps = (state, props) => {
  const { corporateAccount } = props;
  return {
    token: state.auth.token,
    formValues: getFormValues("corporateAccountForm")(state) || {},
    selectedAgencies: state.selectedAgencies.selectedAgencies,
    initialValues: getInitialValues(corporateAccount),
  };
};

let getFinancialMonths = () => {
  let selectValues = [];

  for (let month = 0; month < 12; month++) {
    let placeHolderDate = moment()
      .startOf("year")
      .set("month", month);
    selectValues.push({
      name: placeHolderDate.format("MMMM"),
      id: placeHolderDate.format("M"),
    });
  }

  return selectValues;
};

let CorporateAccountForm = ({
  handleSubmit,
  corporateAccount,
  corporateAccountUsers,
  companies,
  submitting,
  buttonText,
  formValues,
  token,
  slug = false,
  uploadCorporateAccountLogoImage,
  corporatePermissions,
  ...props
}) => {
  const [logoFile, setLogoFile] = useState(null);
  const [printLogoFile, setPrintLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingPrintLogo, setUploadingPrintLogo] = useState(false);

  const handleRemoveBookingDesks = e => {
    if (
      e &&
      props.initialValues.booking_desks &&
      e.length < props.initialValues.booking_desks.length
    ) {
      props.change(
        "removeBookingDesks",
        _.differenceWith(props.initialValues.booking_desks, e, _.isEqual)
      );
    }
    if (e === null) {
      props.change("removeBookingDesks", props.initialValues.booking_desks);
    }
  };

  const formatFormValues = (
    formValuesForPost,
    formValues,
    key,
    valueKey,
    isObject = false,
    objectKey = ""
  ) => {
    if (typeof formValues[key] !== "undefined") {
      let value =
        isObject && formValues[valueKey]
          ? formValues[valueKey][objectKey]
          : formValues[valueKey];
      if (typeof value !== "undefined" && value !== null) {
        formValuesForPost[key] =
          isObject && formValues[valueKey]
            ? formValues[valueKey][objectKey]
            : formValues[valueKey];
      }
    }
  };

  const formatFormValuesArray = (
    formValuesForPost,
    formValues,
    key,
    valueKey,
    includesKey
  ) => {
    if (typeof formValues[key] !== "undefined") {
      formValuesForPost[valueKey] = formValues[key].includes(includesKey)
        ? 1
        : 0;
    }
  };

  const formatFormValuesFromArray = (
    formValuesForPost,
    formValues,
    key,
    valueKey
  ) => {
    if (typeof formValues[key] !== "undefined") {
      formValuesForPost[valueKey] =
        typeof formValues[key] === "string"
          ? {}
          : formValues[key].map(arr => {
              return {
                value: arr.value,
              };
            });
    }
  };

  const submitForm = formValues => {
    let formValuesForPost = {};
    let initialValuesFormatted = {};
    let formFieldKeys = [
      { key: "account_name", valueKey: "account_name" },
      {
        key: "colour_scheme_classname",
        valueKey: "colour_scheme_classname",
        isObject: true,
        objectKey: "value",
      },
      {
        key: "company",
        valueKey: "company",
        isObject: true,
        objectKey: "value",
      },
      { key: "logo_filename", valueKey: "logo_filename" },
      {
        key: "financial_year_start_month",
        valueKey: "financial_year_start_month",
        isObject: true,
        objectKey: "value",
      },
      { key: "is_pharmaceutical", valueKey: "is_pharmaceutical" },
      { key: "export_net", valueKey: "export_net" },
      {
        key: "show_internal_external_on_brief",
        valueKey: "show_internal_external_on_brief",
      },
      { key: "authorising_email", valueKey: "authorising_email" },
      {
        key: "email_notification_override",
        valueKey: "email_notification_override",
      },
      {
        key: "client_facing_email_notification_override",
        valueKey: "client_facing_email_notification_override",
      },
      {
        key: "venue_facing_email_notification_override",
        valueKey: "venue_facing_email_notification_override",
      },
      {
        key: "max_delegates_triggers_authorisation_workflow",
        valueKey: "max_delegates_triggers_authorisation_workflow",
      },

      {
        key: "review_items",
        isArray: true,
        valueKey: "requires_abpi",
        includesKey: "requires_abpi",
      },
      {
        key: "review_items",
        isArray: true,
        valueKey: "internal_external_split",
        includesKey: "internal_external_split",
      },

      {
        key: "corporate_account_permissions",
        valueKey: "permission",
        fromArray: true,
      },
      { key: "booking_desks", valueKey: "booking_desks", fromArray: true },
    ];
    formFieldKeys.forEach(fieldKey => {
      if (props.type === "edit" && fieldKey.key === "booking_desks") {
        // api does a sync so must post what's actually used or don't post at all
        if (formValues.booking_desks) {
          formValuesForPost.booking_desks = formValues.booking_desks.map(
            desk => ({ value: desk.value })
          );
        } else {
          formValuesForPost.booking_desks = [];
        }
        if (props.initialValues.booking_desks) {
          initialValuesFormatted.booking_desks = props.initialValues.booking_desks.map(
            desk => ({ value: desk.value })
          );
        } else {
          initialValuesFormatted.booking_desks = [];
        }
        return;
      }

      if (fieldKey.fromArray) {
        formatFormValuesFromArray(
          formValuesForPost,
          formValues,
          fieldKey.key,
          fieldKey.valueKey
        );
        if (props.type === "edit") {
          formatFormValuesFromArray(
            initialValuesFormatted,
            props.initialValues,
            fieldKey.key,
            fieldKey.valueKey
          );
        }
      } else if (fieldKey.isArray) {
        formatFormValuesArray(
          formValuesForPost,
          formValues,
          fieldKey.key,
          fieldKey.valueKey,
          fieldKey.includesKey
        );
        if (props.type === "edit") {
          formatFormValuesArray(
            initialValuesFormatted,
            props.initialValues,
            fieldKey.key,
            fieldKey.valueKey,
            fieldKey.includesKey
          );
        }
      } else {
        formatFormValues(
          formValuesForPost,
          formValues,
          fieldKey.key,
          fieldKey.valueKey,
          fieldKey.isObject || false,
          fieldKey.objectKey || ""
        );
        if (props.type === "edit") {
          formatFormValues(
            initialValuesFormatted,
            props.initialValues,
            fieldKey.key,
            fieldKey.valueKey,
            fieldKey.isObject || false,
            fieldKey.objectKey || ""
          );
        }
      }
    });
    let fileToUpload = logoFile;
    setLogoFile(null);
    if (props.type === "create") {
      return processFormValuesPost(
        props,
        formValuesForPost,
        props.initialValues,
        fileToUpload,
        "logo_file"
      );
    }
    const changedValues = getChangedValues(
      formValuesForPost,
      initialValuesFormatted
    );
    return processFormValuesPatch(
      props,
      changedValues,
      props.initialValues,
      fileToUpload,
      "logo_file"
    );
  };

  let uploadFile = (file, isPrint = false) => {
    if (file) {
      if (isPrint) {
        setUploadingPrintLogo(true);
      } else {
        setUploading(true);
      }
      let formData = new FormData();
      if (isPrint) {
        formData.append("print_image", file[0]);
        formData.append("is_print", 1);
      } else {
        formData.append("image", file[0]);
      }
      uploadCorporateAccountLogoImage(slug, token, formData).then(() => {
        setUploadingPrintLogo(false);
        setUploading(false);
      });
      if (isPrint) {
        props.change("print_logo_file", file[0]);
      } else {
        props.change("logo_file", file[0]);
      }
    } else {
      setUploading(false);
      setUploadingPrintLogo(false);
    }
  };

  const getBookingDeskOptions = () => {
    let options = [];
    if (
      corporateAccount &&
      corporateAccount.company &&
      corporateAccount.company.agency
    ) {
      if (
        props.bookingDesks[corporateAccount.company.agency.name] &&
        isArray(props.bookingDesks[corporateAccount.company.agency.name])
      ) {
        props.bookingDesks[corporateAccount.company.agency.name].map(
          bookingDesk => {
            options.push({
              label: bookingDesk.name,
              value: bookingDesk.id,
            });
          }
        );
      }
    }
    return options;
  };

  const getPermissionsOptions = () => {
    let options = [];
    if (corporatePermissions && isArray(corporatePermissions)) {
      corporatePermissions.map(permission => {
        options.push({
          label: permission.display_name,
          value: permission.permission,
        });
      });
    }
    return options;
  };

  return (
    <div>
      <div className=" border-gray-200 sm:rounded-lg">
        <form className="border-none pb-2" onSubmit={handleSubmit(submitForm)}>
          <div className="row">
            <div className={"col col-w-full md:col-w-1/2"}>
              <Field
                name="account_name"
                label="Name of Corporate Account"
                component={renderFormInput}
              />
            </div>
            <div className={"col col-w-full md:col-w-1/2"}>
              <Field
                name="company"
                label="Linked Company"
                white
                component={SmartSelect}
                options={
                  companies.length > 0 &&
                  companies
                    .filter(company => company.corporate_account_id === null)
                    .map(item => {
                      return {
                        label: item.name,
                        value: item.id,
                      };
                    })
                }
              />
            </div>
          </div>
          <hr className="my-6" />
          <div className={"row"}>
            <div className={"col col-w-full mb-6"}>
              <h3
                className={
                  "mb-4 md:flex md:flex-row md:justify-between md:items-center uppercase"
                }
              >
                Configuration
              </h3>
            </div>
          </div>
          <div className={"row"}>
            <div className={"col col-w-full md:col-w-1/2 mb-6"}>
              <Field
                name="financial_year_start_month"
                label="Financial Year Start Month"
                white
                component={SmartSelect}
                options={
                  getFinancialMonths().length > 0 &&
                  getFinancialMonths().map(item => ({
                    label: item.name,
                    value: item.id,
                  }))
                }
              />
              <Field
                name="colour_scheme_classname"
                label="Colour Highlight for Corporate EDGE"
                white
                component={SmartSelect}
                options={colourSchemeOptions}
              />

              {corporateAccount ? (
                <Field
                  label={"Booking Desks"}
                  name={"booking_desks"}
                  isMulti={true}
                  component={SmartSelect}
                  onChange={e => handleRemoveBookingDesks(e)}
                  options={getBookingDeskOptions()}
                />
              ) : null}
              {props.type === "edit" ?  <Field
                label={"Permissions"}
                name={"corporate_account_permissions"}
                isMulti={true}
                component={SmartSelect}
                options={getPermissionsOptions()}
              /> : null}


              <Field
                name="is_pharmaceutical"
                label="Is Pharmaceutical"
                component={Toggle}
              />

              <Field
                name="export_net"
                label="Export as Net?"
                component={Toggle}
              />

              {false ? (
                <>
                  {formValues.is_pharmaceutical ? (
                    <>
                      <p className={"text-sm"}>
                        {" "}
                        Please configure the following fields specifically for
                        Pharmaceutical companies{" "}
                      </p>

                      <Field
                        name="show_internal_external_on_brief"
                        label="Brief asks whether meeting is internal or external"
                        component={Toggle}
                      />
                      <Field
                        name="review_items"
                        label={""}
                        component={Checkbox}
                        options={[
                          {
                            text: "Requires ABPI compliance?",
                            value: "requires_abpi",
                          },
                          {
                            text:
                              "RFP delegate count is split into internal + external",
                            value: "internal_external_split",
                          },
                        ]}
                        validate={v.required}
                      />
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          <div className="row">
            {slug ? (
              <>
                <div className={"col col-w-full md:col-w-1/2 mb-6"}>
                  <label className="mb-1 block font-normal font-heading text-sm uppercase">
                    Logo Upload
                  </label>
                  <DropzoneArea
                    type="image"
                    minWidth={"200px"}
                    minHeight={"100px"}
                    onDrop={file => uploadFile(file, false)}
                    uploading={uploading}
                  />
                  <div className="hidden">
                    <Field
                      name="logo_filename"
                      label="File Name"
                      component={renderFormInput}
                    />
                  </div>
                  {logoFile ? (
                    <div className={"col col-w-full md:col-w-1/2 mb-6"}>
                      <img
                        src={window.URL.createObjectURL(logoFile)}
                        alt="logo"
                      />
                    </div>
                  ) : null}
                  {!logoFile &&
                  corporateAccount &&
                  corporateAccount?.logo_link &&
                  corporateAccount?.cdn_path ? (
                    <img
                      src={corporateAccount.cdn_path}
                      alt={
                        corporateAccount
                          ? corporateAccount.account_name
                          : "Logo"
                      }
                    />
                  ) : null}
                  <p className={"font-small"}>
                    The logo is used on the website header when users are logged
                    in to the Corporate account. Please ensure it is a
                    transparent/white PNG file 200px x 100px
                  </p>
                </div>
                <div className={"col col-w-full md:col-w-1/2 mb-6"}>
                  <label className="mb-1 block font-normal font-heading text-sm uppercase">
                    Print Logo Upload
                  </label>
                  <DropzoneArea
                    type="image"
                    minWidth={"200px"}
                    minHeight={"100px"}
                    onDrop={file => uploadFile(file, true)}
                    uploading={uploadingPrintLogo}
                  />
                  <div className="hidden">
                    <Field
                      name="print_logo_filename"
                      label="File Name"
                      component={renderFormInput}
                    />
                  </div>
                  {printLogoFile ? (
                    <div className={"col col-w-full md:col-w-1/2 mb-6"}>
                      <img
                        src={window.URL.createObjectURL(printLogoFile)}
                        alt="logo"
                      />
                    </div>
                  ) : null}
                  {!printLogoFile &&
                  corporateAccount &&
                  corporateAccount?.print_logo_link &&
                  corporateAccount?.cdn_path_print ? (
                    <img
                      src={corporateAccount.cdn_path_print}
                      alt={
                        corporateAccount
                          ? corporateAccount.account_name
                          : "Print Logo"
                      }
                    />
                  ) : null}
                  <p className={"font-small"}>
                    The logo is used on PDF exports associated with this
                    corporate account. Please ensure it is a transparent/white
                    PNG file 200px x 100px
                  </p>
                </div>
              </>
            ) : null}
          </div>

          <hr className={"my-6"} />

          <div className={"row"}>
            <div className={"col col-w-full mb-6"}>
              <h3
                className={
                  "mb-4 md:flex md:flex-row md:justify-between md:items-center uppercase"
                }
              >
                Email Overrides
              </h3>
              <p>
                All outbound emails to agency, client or venues can be
                overridden here.
              </p>
            </div>
          </div>

          <div className="row">
            <div className={"col col-w-full md:col-w-1/2 mb-6"}>
              <Field
                name="email_notification_override"
                label="Agency Email Notification Override - instead of default distribution list (info@ / enquiries@)"
                component={renderFormInput}
                validate={v.email}
              />
              <Field
                name="client_facing_email_notification_override"
                label="Client Facing Email Notification Override"
                component={renderFormInput}
                validate={v.email}
              />
              <Field
                name="venue_facing_email_notification_override"
                label="Venue Facing Email Notification Override"
                component={renderFormInput}
                validate={v.email}
              />
            </div>
          </div>

          <hr className={"my-6"} />

          <div className={"row"}>
            <div className={"col col-w-full md:col-w-1/2 mb-6"}>
              <h3
                className={
                  "mb-4 md:flex md:flex-row md:justify-between md:items-center uppercase"
                }
              >
                Authorisation
              </h3>
              <Field
                name={"max_delegates_triggers_authorisation_workflow"}
                label="Max delegates to trigger authorisation workflow"
                wrapperClassName="w-full mr-2"
                component={Input}
                type={"number"}
                min={0}
                step={1}
                normalize={val => (val || "").replace(/[^\d]/g, "")}
              />
              <br />
              <Field
                name="authorising_email"
                label="Authorising Email"
                component={renderFormInput}
              />
            </div>
          </div>

          <div className="row min-w-full">
            <div className={"col col-w-full "}>
              <div className={"corp-form-buttons"}>
                <ButtonPrimary type="submit">
                  {submitting ? (
                    <Loading inline />
                  ) : buttonText ? (
                    buttonText
                  ) : (
                    "Create Corporate Account"
                  )}
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

CorporateAccountForm = reduxForm({
  form: "corporateAccountForm",
  enableReinitialize: true,
})(CorporateAccountForm);

export default connect(mapStateToProps, { uploadCorporateAccountLogoImage })(
  CorporateAccountForm
);
